import {
  DialogContent,
  Divider,
  Typography,
  MenuItem,
  Stack,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import DialogHeader from 'common/DialogHeader/DialogHeader';
import dateFormat from 'dateformat';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SubmitButton from 'common/SubmitButton/SubmitButton';
import ControlTextField from 'common/ControlTextField/ControlTextField';
import ControlSelect from 'common/ControlSelect/ControlSelect';
import ProjectStatus from 'components/AddWorkingHoursForm/enums';
import getProjectPhaseHelpers from 'components/EditWorkingHoursForm/getProjectPhaseHelpers';
import { useGetProjectsPreviewQuery } from 'store/projects/projects';
import { EmployeeProjectPhase } from 'components/AddWorkingHoursForm/interfaces';
import { isAfter } from 'date-fns';
import isValidStringTime from 'helpers/isValidStringTime/isValidStringTime';
import useScheduleCalendarContext from '../context/useScheduleCalendarContext';
import schema from './AddWorkingHoursForm.schema';
import { AddWorkingHoursFormProps } from './interfaces';
import DateToSelect from './DateToSelect/DateToSelect';
import DateFromSelect from './DateFromSelect/DateFromSelect';

const validate = zodResolver(schema);
const minDescriptionLength = 32;

export default function AddWorkingHoursForm({
  employeeId,
  onSubmit,
  isSubmitting,
}: AddWorkingHoursFormProps) {
  const dateFromRef = useRef<HTMLInputElement | null>(null);
  const dateToRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const {
    draftScheduleWorkingHours,
    updateEndDateOfDraftScheduleWorkingHours,
    updateStartDateOfDraftScheduleWorkingHours,
    handleOnCloseDraftForm,
  } = useScheduleCalendarContext();

  const { data: employeeProjects = [] } = useGetProjectsPreviewQuery({
    status: ProjectStatus.active,
    employee: employeeId,
  });

  const {
    employeeProjectsPhases,
    getProjectPhaseText,
    getProjectPhaseKey,
    getProjectPhaseValue,
  } = getProjectPhaseHelpers(employeeProjects);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm({
    resolver: validate,
    mode: 'onChange',
    defaultValues: {
      project: '',
      description: '',
    },
  });

  const { project, description } = watch();

  const isDisabled =
    Object.values(errors).some((error) => error) ||
    !project ||
    !description ||
    !draftScheduleWorkingHours ||
    !isAfter(
      new Date(draftScheduleWorkingHours.endDate),
      new Date(draftScheduleWorkingHours.startDate),
    ) ||
    !isValidStringTime(dateToRef?.current?.value || '') ||
    !isValidStringTime(dateFromRef?.current?.value || '');

  return (
    <>
      <DialogHeader
        title={t('project_tracking.add_hours')}
        onClose={handleOnCloseDraftForm}
        subTitleComponent={
          <Typography variant="bold" color="secondary">
            {draftScheduleWorkingHours &&
              dateFormat(draftScheduleWorkingHours.startDate, 'dd mmmm')}
          </Typography>
        }
      />
      <Divider />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: 3,
            mt: 2,
          }}
        >
          <ControlSelect
            name="project"
            control={control}
            label={t('inputs.project')}
            errors={errors}
            required
            renderOptions={employeeProjectsPhases.map(
              (projectPhaseData: EmployeeProjectPhase) => (
                <MenuItem
                  key={getProjectPhaseKey(projectPhaseData)}
                  value={getProjectPhaseValue(projectPhaseData)}
                >
                  {getProjectPhaseText(projectPhaseData)}
                </MenuItem>
              ),
            )}
          />
          <Stack direction="row" spacing={3}>
            <DateFromSelect
              inputRef={dateFromRef}
              date={
                draftScheduleWorkingHours?.startDate
                  ? new Date(draftScheduleWorkingHours.startDate)
                  : new Date()
              }
              onDateChange={updateStartDateOfDraftScheduleWorkingHours}
            />
            <DateToSelect
              inputRef={dateToRef}
              date={
                draftScheduleWorkingHours?.endDate
                  ? new Date(draftScheduleWorkingHours.endDate)
                  : new Date()
              }
              dateFrom={
                draftScheduleWorkingHours?.startDate
                  ? new Date(draftScheduleWorkingHours.startDate)
                  : new Date()
              }
              onDateChange={(newEndDate) =>
                updateEndDateOfDraftScheduleWorkingHours(newEndDate, true)
              }
            />
          </Stack>
          <ControlTextField
            name="description"
            control={control}
            label={t('label.description')}
            errors={errors}
            required
            multiline
            minRows={4}
            customHelperText={t('project_tracking.error_characters_count', {
              count: description.trim().length,
              minLength: minDescriptionLength,
            })}
          />
        </DialogContent>
        <Divider sx={{ my: 1.5 }} />
        <DialogContent sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SubmitButton
            isLoading={isSubmitting}
            disabled={isDisabled}
            label="project_tracking.log_time"
            noIcon
          />
        </DialogContent>
      </form>
    </>
  );
}
